// ============================================================================
// Safe HTML templating.
//
// The old code interpolated user input straight into template literals and
// assigned to innerHTML, which meant an applicant could put a <script> tag in
// their pitch and run it in an admin's browser. Rather than remember to escape
// at ~200 call sites, escaping is now the default: the `html` tag escapes
// every interpolated value, and you have to opt out explicitly with raw().
// ============================================================================

const ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

/** Escape a value for insertion into HTML text or a quoted attribute. */
export function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[&<>"']/g, (c) => ENTITIES[c]);
}

/**
 * Marks a string as already-safe HTML so `html` won't escape it again.
 * Only ever wrap markup YOU generated — never anything derived from user
 * input, a database field, or a URL parameter.
 */
class SafeHtml {
  constructor(value) { this.value = value; }
  toString() { return this.value; }
}

export function raw(value) {
  return new SafeHtml(value == null ? '' : String(value));
}

/**
 * Tagged template that escapes every interpolation.
 *
 *   html`<p>${userInput}</p>`            // escaped, safe
 *   html`<div>${raw(renderRows(items))}</div>`  // opt out for nested markup
 *
 * Arrays are joined with no separator, so you can map straight into it:
 *   html`<ul>${items.map(i => html`<li>${i.name}</li>`)}</ul>`
 */
export function html(strings, ...values) {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) {
    out += stringify(values[i]) + strings[i + 1];
  }
  return raw(out);
}

function stringify(value) {
  if (value === null || value === undefined || value === false) return '';
  if (value instanceof SafeHtml) return value.value;
  if (Array.isArray(value)) return value.map(stringify).join('');
  return escapeHtml(value);
}

/**
 * Build a safe href. Blocks javascript: and data: URLs, which are the usual
 * way a link turns into script execution — relevant because creators submit
 * arbitrary proof URLs that admins then click.
 */
export function safeUrl(url) {
  const s = String(url ?? '').trim();
  if (!s) return '';
  if (/^(https?:|mailto:|\/|#)/i.test(s)) return s;
  return '';
}
